<template>
  <Table.Base
    :id="TableTypes.Explorer"
    ref="componentRef"
    class="h-full overflow-hidden flex flex-col"
  >
    <ExplorerTableHeaders
      :show-checkbox="showCheckbox"
      :checkbox-classes="checkboxClasses"
      :checkbox-icon-classes="checkboxIconClasses"
      :header-icon-classes="headerIconClasses"
      :is-sort="isSort"
      @sort="onSort"
      @toggle-checkbox="toggleCheckbox"
    />

    <ExplorerTableEmpty v-if="showSkeleton" />

    <template v-else>
      <RecycleScroller
        v-if="items?.length"
        :items="items"
        :item-size="itemSize"
        v-slot="{ item }: { item: Item }"
        ref="container"
        class="h-full overflow-y-auto!"
      >
        <ExplorerTableRow
          :item="item"
          :show-checkbox="showCheckbox"
          :checkbox-classes="checkboxClasses"
          :is-selected="table.isRowSelected(item.id)"
          @row-click="handleRowClick"
          @right-click="(event, item) => handleRightClick(event, item, items!)"
          @folder-click="handleFolderClick"
          @name-click="handleNameClick"
          @timestamp-click="handleTimestampClick"
          @three-dots-click="handleThreeDotsClick"
          @context-menu-click="
            (event, item) => handleContextMenuClick(event, item, items!)
          "
        />
      </RecycleScroller>

      <NoFiles v-else />
    </template>
  </Table.Base>

  <div
    v-if="contextMenuComponent"
    :style="contextMenuStyles"
    class="fixed pointer-events-auto z-100"
    id="contextMenu"
    :ref="
      (el) => {
        contextMenuRef = el as HTMLDivElement | null
      }
    "
  >
    <FileContextMenu
      v-if="isFileContextMenu && selectedFiles[0]"
      :file="selectedFiles[0]"
    />
    <MultipleFilesContextMenu
      v-if="isMultipleFilesContextMenu"
      :files="selectedFiles"
    />
    <FolderContextMenu v-if="isFolderContextMenu" :items="selectedItems" />
    <MixedItemsContextMenu
      v-if="isMixedItemsContextMenu"
      :items="selectedItems"
    />
  </div>
</template>

<script setup lang="ts">
import { CopyrightStatus, type Item } from '@/shared/graphql/router/graphql'
import { isFile, isFolder } from '@/features/explorer/utils/itemGuards'
import { ref, computed, watchEffect, onBeforeUnmount } from 'vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useRoute, useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { Table } from '@dacr/ui'
import { TableTypes } from '@/shared/types'
import { useExplorerSort } from '../composables/useExplorerSort'
import { useExplorerSelection } from '../composables/useExplorerSelection'
import { useExplorerContextMenu } from '../composables/useExplorerContextMenu'
import ExplorerTableHeaders from './ExplorerTableHeaders.vue'
import ExplorerTableRow from './ExplorerTableRow.vue'
import ExplorerTableEmpty from './ExplorerTableEmpty.vue'
import NoFiles from './NoFiles.vue'
import FileContextMenu from './FileContextMenu.vue'
import MultipleFilesContextMenu from './MultipleFilesContextMenu.vue'
import FolderContextMenu from './FolderContextMenu.vue'
import MixedItemsContextMenu from './MixedItemsContextMenu.vue'

const props = defineProps<{
  items?: Item[]
}>()
const emit = defineEmits<{
  sort: [value: readonly [string, 'asc' | 'desc']]
  scroll: [event: Event]
}>()

const device = useDevice()
const router = useRouter()
const route = useRoute()
const { open } = useDialog()

const itemsRef = computed(() => props.items)
const { table, selectedItems, selectedFiles } = useExplorerSelection(itemsRef)
const { isSort, headerIconClasses, handleSort } = useExplorerSort()

const isProcessingFile = (item: Item) => isFile(item) && !item.copyright?.id

const {
  contextMenuComponent,
  contextMenuRef,
  componentRef,
  contextMenuStyles,
  isFileContextMenu,
  isMultipleFilesContextMenu,
  isFolderContextMenu,
  isMixedItemsContextMenu,
  handleRightClick,
  handleContextMenuClick,
} = useExplorerContextMenu(isProcessingFile)

const container = ref()

const showSkeleton = computed(
  () => props.items === null || props.items === undefined
)

const itemSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 64 : 40
)

// Checkbox
const showCheckbox = ref(device.isMobile.value || device.isTabletVertical.value)

const toggleCheckbox = () => {
  table.resetSelection()
  showCheckbox.value = !showCheckbox.value
}

const checkboxClasses = computed(() => ({
  'tablet-h:visible!': table.selectedRows.value.length > 0,
}))

const checkboxIconClasses = computed(() => ({
  ...(showCheckbox.value && {
    'bg-green-900': true,
    'stroke-white': true,
  }),
  ...(!showCheckbox.value && {
    'stroke-gray-800': true,
  }),
}))

watchEffect(() => {
  showCheckbox.value = device.isDesktop.value || device.isTabletHorizontal.value
})

// Sort
const onSort = (key: string) => {
  const result = handleSort(key)
  emit('sort', result)
}

// Row handlers
const handleRowClick = (item: Item) => {
  if (device.isDesktop.value || device.isTabletHorizontal.value) return
  handleNameClick(item)
}

const handleNameClick = (item: Item) => {
  if (isFile(item) && !item.copyright?.id) return
  if (isFile(item) && item.copyright?.status === CopyrightStatus.Inactive)
    return

  table.resetSelection()

  if (isFile(item)) {
    router.push(`/file/${item.copyright?.id}`)
  } else {
    handleFolderClick(item)
  }
}

const handleFolderClick = (item: Item) => {
  if (isFile(item)) return

  table.resetSelection()

  router.push({
    name: 'FileExplorerFolderView',
    params: {
      path: [...(route.params.path || []), item.name],
    },
  })
}

const handleTimestampClick = (item: Item) => {
  if (!isFile(item)) return
  if ((item.copyright.registrations.length || 0) < 2) return

  open(Dialogs.Registrations, {
    props: { file: item },
  })
}

const handleThreeDotsClick = (item: Item) => {
  if (isFile(item)) {
    open(Dialogs.FileActions, {
      props: { file: item },
    })
  } else {
    open(Dialogs.FolderActions, {
      props: { folder: item },
    })
  }
}

// Scroll
const handleScroll = (event: Event) => {
  emit('scroll', event)
}

watchEffect(() => {
  container.value?.$el.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  container.value?.$el.removeEventListener('scroll', handleScroll)
})
</script>
